/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import type { EntityTypeName } from '../core/types';

interface CrmSubscriptionContextType {
  /**
   * Register a set of entity types for a caller. Returns an unregister function.
   * Multiple callers can register simultaneously; the provider sends the union
   * to the server via the raw subscribe/unsubscribe transport.
   */
  addSubscription: (id: string, entityTypes: EntityTypeName[]) => void;
  removeSubscription: (id: string) => void;
}

const CrmSubscriptionContext = createContext<CrmSubscriptionContextType | null>(
  null
);

interface CrmSubscriptionProviderProps {
  subscribe: (entityTypes: EntityTypeName[]) => void;
  unsubscribe: () => void;
  children: ReactNode;
}

/** Compare two sorted string arrays for equality. */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

export function CrmSubscriptionProvider({
  subscribe,
  unsubscribe,
  children,
}: CrmSubscriptionProviderProps) {
  // Registry: callerId → entity types requested by that caller
  const registryRef = useRef<Map<string, EntityTypeName[]>>(new Map());
  // Track the last sent subscription to skip redundant subscribe() calls
  const lastSentRef = useRef<EntityTypeName[] | null>(null);
  // Whether a microtask-deferred sync is already pending
  const syncPendingRef = useRef(false);

  const syncSubscription = useCallback(() => {
    // Defer the actual sync to a microtask so that rapid remove+add pairs
    // (e.g., React effect cleanup followed by setup in the same render cycle)
    // are batched into a single subscribe() call. Without this, changing a
    // caller's entity types briefly sends unsubscribe() then subscribe(),
    // which can drop restricted WebSocket events in the gap.
    if (syncPendingRef.current) {
      return;
    }
    syncPendingRef.current = true;
    queueMicrotask(() => {
      syncPendingRef.current = false;
      const registry = registryRef.current;
      if (registry.size === 0) {
        if (lastSentRef.current !== null) {
          lastSentRef.current = null;
          unsubscribe();
        }
        return;
      }
      // Compute the union of all registered entity types
      const union = new Set<EntityTypeName>();
      for (const types of registry.values()) {
        for (const t of types) {
          union.add(t);
        }
      }
      const sorted = [...union].sort();
      // Skip if the union hasn't changed since the last subscribe() call
      if (
        lastSentRef.current !== null &&
        arraysEqual(lastSentRef.current, sorted)
      ) {
        return;
      }
      lastSentRef.current = sorted;
      subscribe(sorted);
    });
  }, [subscribe, unsubscribe]);

  const addSubscription = useCallback(
    (id: string, entityTypes: EntityTypeName[]) => {
      registryRef.current.set(id, entityTypes);
      syncSubscription();
    },
    [syncSubscription]
  );

  const removeSubscription = useCallback(
    (id: string) => {
      registryRef.current.delete(id);
      syncSubscription();
    },
    [syncSubscription]
  );

  const value = useMemo(
    () => ({ addSubscription, removeSubscription }),
    [addSubscription, removeSubscription]
  );

  return (
    <CrmSubscriptionContext.Provider value={value}>
      {children}
    </CrmSubscriptionContext.Provider>
  );
}

/**
 * Access the CRM WebSocket subscription API.
 * Returns null if no provider is mounted (e.g., outside TeamProvider or WS disabled).
 */
export function useCrmSubscription(): CrmSubscriptionContextType | null {
  return useContext(CrmSubscriptionContext);
}
